import { useSuspenseQuery } from '@apollo/client/react';
import { Fragment } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CompanyDocument } from 'src/graphql/actions/company/company.query';
import { CompanyActiveSolutionsDocument } from 'src/graphql/actions/company/companyActiveSolutions.query';
import { withSuspense } from 'src/hoc';
import { Separator } from '@/components/ui/separator';

import { HomeBanner, ActionCard, HomeSkeleton } from '.';

import { organizationStatusMap, solutionsMap } from '../model';

const prioritySolutionsType: Record<'backup' | 'softwareEscrow' | 'saasEscrow' | 'continuity' | 'empty', number> = {
  empty: 0,
  backup: 1,
  softwareEscrow: 2,
  saasEscrow: 3,
  continuity: 4,
};

const HomeContainer = () => {
  const { data: solutionsData } = useSuspenseQuery(CompanyActiveSolutionsDocument, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: companyData } = useSuspenseQuery(CompanyDocument, {
    fetchPolicy: 'cache-and-network',
  });

  const activeSolutionsMap: Record<keyof typeof solutionsMap, number> = {
    backup: solutionsData.activeBackups.nodesCount,
    softwareEscrow: solutionsData.activeSoftwareEscrows.nodesCount,
    saasEscrow: solutionsData.activeSaasEscrows.nodesCount,
    continuity: solutionsData.activeContinuityEscrows.nodesCount,
  };
  const solutionsItems = Object.keys(solutionsMap).map((key) => {
    const activeValue = activeSolutionsMap[key as keyof typeof activeSolutionsMap];

    return {
      id: key,
      ...solutionsMap[key as keyof typeof solutionsMap],
      activeValue,
    };
  });
  const currentPrioritySolution = solutionsItems.reduce((acc, { id, activeValue }) => {
    if (!acc) return id;

    return id in prioritySolutionsType &&
      Boolean(activeValue) &&
      prioritySolutionsType[id as keyof typeof prioritySolutionsType] >
        prioritySolutionsType[acc as keyof typeof prioritySolutionsType]
      ? (id as keyof typeof prioritySolutionsType)
      : acc;
  }, 'empty' as string);
  const currentOrgStatus = organizationStatusMap[currentPrioritySolution as keyof typeof organizationStatusMap];

  const onGetVerified = () => window.open('https://codekeeper.co/verification', '_blank');

  return (
    <Fragment>
      <div className="flex flex-wrap sm:flex-nowrap w-full lg:grid lg:grid-cols-4 gap-4 mb-8">
        <HomeBanner
          {...currentOrgStatus}
          subtitle={companyData.company.companyName ?? ''}
          className="lg:col-span-3 w-full"
        />

        <div className="shrink-0 w-full sm:w-[232px] lg:w-auto lg:col-span-1 p-6 border border-border rounded-lg shadow-xs">
          <div className="text-base sm:text-lg font-bold text-card-foreground mb-1">Assure your customers</div>
          <p className="text-xs text-muted-foreground mb-6">Show your customers that their data is secured.</p>
          <Button
            variant="outline"
            disabled={currentPrioritySolution === 'empty'}
            size="sm"
            onClick={onGetVerified}
            className="w-full"
          >
            Learn more <ArrowUpRight />
          </Button>
        </div>
      </div>

      <Separator className="mb-8" />

      <div className="space-y-3 mb-6">
        <h2 className="text-lg text-foreground">Your coverage</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {solutionsItems.map(({ id, ...solution }) => (
            <ActionCard key={id} {...solution} />
          ))}
        </div>
      </div>
    </Fragment>
  );
};

export default withSuspense(HomeContainer, HomeSkeleton);
